import React, { useState } from 'react';
import { FaChevronLeft, FaSpinner, FaCheck, FaLayerGroup, FaFileAlt, FaCogs, FaSlidersH, FaArrowRight, FaExclamationCircle } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { submitRequirementForm } from './api';
import useSEO from './useSEO';

// Dynamic Deliverables Mapping per Platform
export const PLATFORM_DELIVERABLES = {
  'Custom Shopify Store & Headless E-Commerce': [
    'Custom Shopify 2.0 Theme Design & Liquid Architecture',
    'Recharge / Skio Subscriptions & Auto-Ship Replenishment',
    'AJAX Slide-Out Cart with Dynamic 1-Click Upsells & Free Shipping Meter',
    'Headless Shopify Hydrogen & Storefront GraphQL API Setup',
    'Multi-Currency Global Checkout (120+ Currencies) & Localized Duties',
    'B2B Wholesale Customer Gated Portal & Custom Tiered Pricing',
    'Klaviyo Email & SMS Marketing Flows + Abandoned Checkout Recovery',
    'Interactive Product Color Swatches & 3D Garment Visualizers'
  ],
  'Cross-Platform Flutter Mobile App': [
    'Native iOS & Android Cross-Platform Deployment (Flutter)',
    'Biometric Authentication (FaceID / Fingerprint) & Apple/Google SSO',
    'Real-Time Live GPS Map Routing & Driver / Parcel Tracking',
    'In-App Cashless Digital Wallet & Stripe / Apple Pay Integration',
    'Firebase Cloud Messaging (FCM) Push Notifications Engine',
    'Offline SQLite / Hive Local Data Caching & Cloud Sync',
    'Camera Vision & Document / Image Upload Scanning',
    'In-App Audio / Video Player & Interactive Media Feed'
  ],
  'Custom SaaS Platform & MVP Launch': [
    'Multi-Tenant Architecture with Dedicated Subdomains',
    'Automated Stripe Billing (Tiered, Usage-Based & Annual Subscriptions)',
    'Role-Based Access Control (RBAC) & Team Invitation System',
    'Interactive Executive Analytics Dashboard with Real-Time Charting',
    'High-Performance REST & GraphQL API Gateway with API Keys',
    'Automated PDF Invoice & Financial Report Generation Engine',
    'Redis Background Task Queue & Asynchronous Job Workers',
    'Audit Trail Logging & Enterprise Security Hardening'
  ],
  'Full-Stack Web Application': [
    'Modern React 18 / Next.js 15 Server-Side Rendering (SSR)',
    'High-Concurrency Node.js & Express / FastAPI Backend',
    'PostgreSQL / MongoDB Database Architecture & Query Indexing',
    'Dynamic Drag-and-Drop Kanban & Content Management UI',
    'WebSocket Live Event Feeds & Zero-Jitter Real-Time Sync',
    'Secure OAuth 2.0 (Google, GitHub, Microsoft) & JWT Session Guards',
    'Global Cloudflare CDN Asset Delivery & Automated SSL',
    'Automated CI/CD Pipeline & Docker Containerization'
  ],
  'High-Performance Backend & APIs': [
    'Microservices Architecture & Containerized Services (Docker)',
    'Ultra-Low Latency REST & GraphQL API Endpoints (<50ms)',
    'Redis In-Memory Caching & API Rate Limiting Guardrails',
    'PostgreSQL / MySQL ACID-Compliant Database Optimization',
    'Stripe Connect / PayPal Payment Webhooks Reconciliation',
    'Asynchronous Job Workers (BullMQ / Celery Queues)',
    'Comprehensive Automated Unit & Integration Testing Suite',
    'Cloud Deployment on AWS (ECS, Lambda, S3, RDS) or GCP'
  ],
  'AI Workflow Automation': [
    'Multi-Node n8n / Make.com / LangChain Pipeline Engineering',
    'Automated Multi-Page Document OCR & Invoice Parsing (LLM Vision)',
    'Autonomous Inbound Lead Enrichment & Cold Outreach Sequences',
    'Social Media Content Generator & Omni-Channel Publisher',
    'Shopify / ERP / CRM Automated Webhook Order Sync',
    'Slack / Discord / Microsoft Teams 1-Click Action Bots',
    'Self-Healing Auto-Retry Queues & Error Alert Logging',
    'Autonomous Competitor Scraping & Market Research Briefings'
  ],
  '24/7 AI Voice Calling Agent': [
    'Ultra-Low Latency Conversational Telephony (<420ms)',
    'Bi-Directional Speech-to-Speech Voice Agent (Retell AI, ElevenLabs, Vapi)',
    'Twilio Voice SIP Trunking & Dynamic Phone Number Provisioning',
    'Real-Time Live Speech Transcription Stream & Sentiment Telemetry',
    'Autonomous Google Calendar & Outlook Meeting Scheduling',
    'Instant CRM Record Sync (HubSpot, Salesforce, Shopify, Zoho)',
    'Multi-Turn Human Interruption Handling & Dynamic Tone Adaptation',
    'Post-Call SMS / WhatsApp Confirmation & Summary Dispatch'
  ],
  'Custom RAG AI Chatbot': [
    'Retrieval-Augmented Generation (RAG) Grounded in Private Docs',
    'Hybrid Semantic Vector Search (Pinecone, pgvector, Qdrant)',
    'Multi-Channel Deployment (Website Widget, WhatsApp, Slack, Telegram)',
    'Multi-Turn Conversational Memory & Context Optimization',
    'Seamless Human Escalation & Tier-1 Support Ticket Triage',
    'Zendesk, Notion & Knowledge Base Live Document Syncing',
    'Customizable System Persona & Guardrail Safety Filters',
    'Comprehensive Chat Analytics & Sentiment Score Dashboard'
  ],
  'App & Web AI Integration': [
    'OpenAI GPT-4o, Claude 3.5 Sonnet & Google Gemini 1.5 Pro APIs',
    'Computer Vision Image Scanning & Dynamic Feature Extraction',
    'Fine-Tuned Domain-Specific Prompt Engineering & System Chains',
    'Local / Private LLM Deployment (Ollama / vLLM / HuggingFace)',
    'AI Predictive Market Forecasting & Data Recommendation Engine',
    'Automated Audio Transcription (Whisper AI) & Text-to-Speech',
    'Real-Time Streaming Token UI Responses with Markdown & Code',
    'Vector Embeddings Caching & Token Cost Optimization'
  ]
};

const ReqForm = () => {
    useSEO({
      title: "Project Discovery & Quote Calculator | Core Spectators",
      description: "Submit your project requirements to calculate timeline and cost estimates for custom Shopify 2.0 storefronts, Flutter mobile apps, SaaS MVPs, or AI automations.",
      keywords: "request project quote, shopify development quote, flutter mobile app cost estimate, saas mvp pricing, software engineering scope estimate, ai workflow quote",
      canonical: "https://corespectators.com/Form",
      schema: {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Core Spectators Project Discovery & Estimation Wizard",
        "url": "https://corespectators.com/Form",
        "description": "Interactive requirement wizard for custom Shopify storefronts, mobile apps, SaaS platforms, and intelligent AI automations."
      }
    });

    const [currentSection, setCurrentSection] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    
    const initialFormData = {
        client_name: '',
        phone: '',
        email: '',
        start_date: '',
        completion_date: '',
        project_description: '',
        target_audience: '',
        competitors: '',
        seo: 'Yes', 
        platform: 'Custom Shopify Store & Headless E-Commerce', 
        pages: [],
        design_preferences: '',
        responsive: 'Yes',
    };
    const [formData, setFormData] = useState(initialFormData);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prevData => {
            const updated = { ...prevData, [name]: value };
            // If platform changes, reset selected deliverables so they match the newly selected platform
            if (name === 'platform' && value !== prevData.platform) {
                updated.pages = [];
            }
            return updated;
        });

        // Clear error for field on edit
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleCheckboxChange = (event) => {
        const { value, checked } = event.target;
        setFormData(prevData => {
            const pages = checked 
                ? [...prevData.pages, value] 
                : prevData.pages.filter(page => page !== value);
            return { ...prevData, pages };
        });

        if (errors.pages) {
            setErrors(prev => ({ ...prev, pages: null }));
        }
    };

    const handleSelectAllDeliverables = () => {
        const currentList = PLATFORM_DELIVERABLES[formData.platform] || [];
        setFormData(prev => ({
            ...prev,
            pages: [...currentList]
        }));
        if (errors.pages) {
            setErrors(prev => ({ ...prev, pages: null }));
        }
    };

    const handleClearAllDeliverables = () => {
        setFormData(prev => ({
            ...prev,
            pages: []
        }));
    };

    // =========================================================================
    // Strict Step-by-Step Validation ("Nothing can be left empty")
    // =========================================================================
    const validateStep = (stepIdx) => {
        const newErrors = {};

        if (stepIdx === 0) {
            if (!formData.client_name || formData.client_name.trim().length < 2) {
                newErrors.client_name = 'Client or Company Name is required (min 2 characters).';
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!formData.email || !emailRegex.test(formData.email.trim())) {
                newErrors.email = 'A valid email address is required.';
            }
            if (!formData.phone || formData.phone.trim().length < 5) {
                newErrors.phone = 'Phone / WhatsApp number is required (min 5 digits).';
            }
            if (!formData.start_date || formData.start_date.trim() === '') {
                newErrors.start_date = 'Target start date is required.';
            }
            if (!formData.completion_date || formData.completion_date.trim() === '') {
                newErrors.completion_date = 'Target completion date is required.';
            } else if (formData.start_date && formData.completion_date < formData.start_date) {
                newErrors.completion_date = 'Completion date cannot be before start date.';
            }
        }

        if (stepIdx === 1) {
            if (!formData.project_description || formData.project_description.trim().length < 10) {
                newErrors.project_description = 'Project description is required (min 10 characters).';
            }
            if (!formData.target_audience || formData.target_audience.trim().length < 3) {
                newErrors.target_audience = 'Target audience is required (e.g. D2C Shoppers, B2B Clients).';
            }
            if (!formData.competitors || formData.competitors.trim().length < 2) {
                newErrors.competitors = 'Reference websites or competitors are required (e.g. Stripe, Shopify).';
            }
        }

        if (stepIdx === 2) {
            if (!formData.platform || formData.platform.trim() === '') {
                newErrors.platform = 'Primary solution platform selection is required.';
            }
            if (!formData.pages || formData.pages.length === 0) {
                newErrors.pages = 'Please select at least one deliverable module for your chosen platform.';
            }
        }

        if (stepIdx === 3) {
            if (!formData.design_preferences || formData.design_preferences.trim().length < 5) {
                newErrors.design_preferences = 'Design & technical preferences are required (min 5 characters).';
            }
            if (!formData.seo || formData.seo.trim() === '') {
                newErrors.seo = 'SEO optimization selection is required.';
            }
            if (!formData.responsive || formData.responsive.trim() === '') {
                newErrors.responsive = 'Responsiveness option is required.';
            }
        }

        setErrors(newErrors);

        const errorKeys = Object.keys(newErrors);
        if (errorKeys.length > 0) {
            toast.error(newErrors[errorKeys[0]]);
            return false;
        }
        return true;
    };

    // Get current platform deliverables list
    const currentPlatformDeliverables = PLATFORM_DELIVERABLES[formData.platform] || [];

    const sections = [
        {
            stepNumber: 1,
            badge: "Step 1 of 4",
            title: "Basic Information & Contact",
            shortName: "1. Contact",
            icon: FaFileAlt,
            content: (
                <div className="space-y-3">
                    <div>
                        <label className="font-semibold block mb-1 text-sm text-gray-800">
                            Client / Company Name: <span className="text-red-600 font-bold">*</span>
                        </label>
                        <input 
                            type="text" 
                            name="client_name"
                            id="client_name" 
                            value={formData.client_name}
                            onChange={handleChange} 
                            placeholder="e.g. Acme Corporation or John Doe" 
                            required 
                            className={`w-full p-3 border rounded-xl bg-white text-gray-900 shadow-sm focus:outline-none focus:ring-2 transition ${
                                errors.client_name ? 'border-red-500 focus:ring-red-500 bg-red-50/20' : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                            }`}
                        />
                        {errors.client_name && (
                            <p className="text-red-600 text-xs mt-1 flex items-center gap-1 font-medium">
                                <FaExclamationCircle className="text-[11px]" /> {errors.client_name}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="font-semibold block mb-1 text-sm text-gray-800">
                            Email Address: <span className="text-red-600 font-bold">*</span>
                        </label>
                        <input 
                            type="email" 
                            name="email" 
                            id="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="e.g. contact@company.com" 
                            required 
                            className={`w-full p-3 border rounded-xl bg-white text-gray-900 shadow-sm focus:outline-none focus:ring-2 transition ${
                                errors.email ? 'border-red-500 focus:ring-red-500 bg-red-50/20' : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                            }`}
                        />
                        {errors.email && (
                            <p className="text-red-600 text-xs mt-1 flex items-center gap-1 font-medium">
                                <FaExclamationCircle className="text-[11px]" /> {errors.email}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="font-semibold block mb-1 text-sm text-gray-800">
                            Phone / WhatsApp Number: <span className="text-red-600 font-bold">*</span>
                        </label>
                        <input 
                            type="tel" 
                            name="phone" 
                            id="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="e.g. +92 333 1234567" 
                            required
                            className={`w-full p-3 border rounded-xl bg-white text-gray-900 shadow-sm focus:outline-none focus:ring-2 transition ${
                                errors.phone ? 'border-red-500 focus:ring-red-500 bg-red-50/20' : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                            }`}
                        />
                        {errors.phone && (
                            <p className="text-red-600 text-xs mt-1 flex items-center gap-1 font-medium">
                                <FaExclamationCircle className="text-[11px]" /> {errors.phone}
                            </p>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                        <div>
                            <label className="font-semibold block mb-1 text-sm text-gray-800">
                                Target Start Date: <span className="text-red-600 font-bold">*</span>
                            </label>
                            <input 
                                type="date" 
                                name="start_date" 
                                id="start_date"
                                value={formData.start_date}
                                onChange={handleChange}
                                min={new Date().toISOString().split('T')[0]}
                                required
                                className={`w-full p-3 border rounded-xl bg-white text-gray-900 shadow-sm focus:outline-none focus:ring-2 transition ${
                                    errors.start_date ? 'border-red-500 focus:ring-red-500 bg-red-50/20' : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                                }`}
                            />
                            {errors.start_date && (
                                <p className="text-red-600 text-xs mt-1 flex items-center gap-1 font-medium">
                                    <FaExclamationCircle className="text-[11px]" /> {errors.start_date}
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="font-semibold block mb-1 text-sm text-gray-800">
                                Target Launch Date: <span className="text-red-600 font-bold">*</span>
                            </label>
                            <input 
                                type="date" 
                                name="completion_date" 
                                id="completion_date"
                                value={formData.completion_date}
                                onChange={handleChange}
                                min={formData.start_date || new Date().toISOString().split('T')[0]}
                                required
                                className={`w-full p-3 border rounded-xl bg-white text-gray-900 shadow-sm focus:outline-none focus:ring-2 transition ${
                                    errors.completion_date ? 'border-red-500 focus:ring-red-500 bg-red-50/20' : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                                }`}
                            />
                            {errors.completion_date && (
                                <p className="text-red-600 text-xs mt-1 flex items-center gap-1 font-medium">
                                    <FaExclamationCircle className="text-[11px]" /> {errors.completion_date}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )
        },
        {
            stepNumber: 2,
            badge: "Step 2 of 4",
            title: "Project Scope & Objectives",
            shortName: "2. Scope",
            icon: FaLayerGroup,
            content: (
                <div className="space-y-3">
                    <div>
                        <label className="font-semibold block mb-1 text-sm text-gray-800">
                            Project Description & Scope Details: <span className="text-red-600 font-bold">*</span>
                        </label>
                        <textarea 
                            name="project_description" 
                            id="project_description"
                            value={formData.project_description}
                            onChange={handleChange} 
                            placeholder="Detail your requirements (e.g. Shopify 2.0 store with subscriptions, Flutter mobile app, multi-tenant SaaS MVP, 24/7 AI voice calling agent)..." 
                            rows="4" 
                            required
                            className={`w-full p-3 border rounded-xl bg-white text-gray-900 shadow-sm focus:outline-none focus:ring-2 transition ${
                                errors.project_description ? 'border-red-500 focus:ring-red-500 bg-red-50/20' : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                            }`}
                        ></textarea>
                        {errors.project_description && (
                            <p className="text-red-600 text-xs mt-1 flex items-center gap-1 font-medium">
                                <FaExclamationCircle className="text-[11px]" /> {errors.project_description}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="font-semibold block mb-1 text-sm text-gray-800">
                            Target Audience / End Users: <span className="text-red-600 font-bold">*</span>
                        </label>
                        <input 
                            type="text" 
                            name="target_audience" 
                            id="target_audience"
                            value={formData.target_audience}
                            onChange={handleChange} 
                            placeholder="e.g. D2C luxury consumers, corporate B2B clients, agency users" 
                            required
                            className={`w-full p-3 border rounded-xl bg-white text-gray-900 shadow-sm focus:outline-none focus:ring-2 transition ${
                                errors.target_audience ? 'border-red-500 focus:ring-red-500 bg-red-50/20' : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                            }`}
                        />
                        {errors.target_audience && (
                            <p className="text-red-600 text-xs mt-1 flex items-center gap-1 font-medium">
                                <FaExclamationCircle className="text-[11px]" /> {errors.target_audience}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="font-semibold block mb-1 text-sm text-gray-800">
                            Reference Websites or Competitor Brands: <span className="text-red-600 font-bold">*</span>
                        </label>
                        <input 
                            type="text" 
                            name="competitors" 
                            id="competitors"
                            value={formData.competitors}
                            onChange={handleChange} 
                            placeholder="e.g. Stripe, Eight Sleep, Colderatti, ElevenLabs" 
                            required
                            className={`w-full p-3 border rounded-xl bg-white text-gray-900 shadow-sm focus:outline-none focus:ring-2 transition ${
                                errors.competitors ? 'border-red-500 focus:ring-red-500 bg-red-50/20' : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                            }`}
                        />
                        {errors.competitors && (
                            <p className="text-red-600 text-xs mt-1 flex items-center gap-1 font-medium">
                                <FaExclamationCircle className="text-[11px]" /> {errors.competitors}
                            </p>
                        )}
                    </div>
                </div>
            )
        },
        {
            stepNumber: 3,
            badge: "Step 3 of 4",
            title: "Platform & Specific Deliverables",
            shortName: "3. Platform",
            icon: FaCogs,
            content: (
                <div className="space-y-3">
                    <div>
                        <label className="font-semibold block mb-1 text-sm text-gray-800">
                            Primary Solution Architecture: <span className="text-red-600 font-bold">*</span>
                        </label>
                        <select 
                            name="platform" 
                            id="platform"
                            value={formData.platform}
                            onChange={handleChange}
                            required 
                            className="w-full p-3 border border-gray-300 rounded-xl bg-white text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 font-bold transition"
                        >
                            <option value="Custom Shopify Store & Headless E-Commerce">🛍️ Custom Shopify 2.0 & Headless Store (Liquid, Hydrogen, Recharge)</option>
                            <option value="Cross-Platform Flutter Mobile App">📱 Cross-Platform Mobile App (Flutter & React Native - iOS / Android)</option>
                            <option value="Custom SaaS Platform & MVP Launch">⚡ Custom SaaS Platform Architecture & Rapid 0-to-1 MVP Launch</option>
                            <option value="Full-Stack Web Application">💻 Full-Stack Web App Development (React 18, Next.js 15, Node.js)</option>
                            <option value="High-Performance Backend & APIs">🔌 High-Performance Backend & REST / GraphQL API Architecture</option>
                            <option value="AI Workflow Automation">🤖 AI Workflow Automation (n8n, Make.com, LangChain Pipelines)</option>
                            <option value="24/7 AI Voice Calling Agent">🎙️ 24/7 AI Voice Calling Agent (Retell AI, ElevenLabs, Twilio Voice)</option>
                            <option value="Custom RAG AI Chatbot">💬 Custom RAG AI Chatbot & Knowledge Base (WhatsApp, Slack, Web)</option>
                            <option value="App & Web AI Integration">🧠 App & Web AI Integration (OpenAI Vision, Claude, Gemini 1.5)</option>
                        </select>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <div>
                                <label className="font-semibold block text-sm text-gray-800">
                                    Deliverables for <span className="text-red-600">{formData.platform.split('&')[0].trim()}</span>: <span className="text-red-600 font-bold">*</span>
                                </label>
                                <p className="text-xs text-gray-500">Showing modules specific to your selected platform ({formData.pages.length} selected):</p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handleSelectAllDeliverables}
                                    className="text-[11px] font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-2.5 py-1 rounded-lg border border-red-200 transition"
                                >
                                    Select All
                                </button>
                                <button
                                    type="button"
                                    onClick={handleClearAllDeliverables}
                                    className="text-[11px] font-semibold text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded-lg transition"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>

                        {errors.pages && (
                            <p className="text-red-600 text-xs mb-2 flex items-center gap-1 font-bold bg-red-50 p-2 rounded-lg border border-red-200">
                                <FaExclamationCircle className="text-xs" /> {errors.pages}
                            </p>
                        )}
                        
                        {/* Dynamic Platform Deliverables Grid */}
                        <div className="max-h-[250px] overflow-y-auto pr-1">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full text-sm">
                                {currentPlatformDeliverables.map(deliverable => {
                                    const isSelected = formData.pages.includes(deliverable);
                                    return (
                                        <div 
                                            key={deliverable} 
                                            onClick={() => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    pages: isSelected 
                                                        ? prev.pages.filter(p => p !== deliverable)
                                                        : [...prev.pages, deliverable]
                                                }));
                                                if (errors.pages) {
                                                    setErrors(prev => ({ ...prev, pages: null }));
                                                }
                                            }}
                                            className={`flex items-start p-2.5 border rounded-xl transition cursor-pointer ${
                                                isSelected
                                                    ? 'bg-red-50/90 border-red-400 text-red-950 font-bold shadow-sm'
                                                    : 'bg-white border-gray-200 text-gray-800 hover:bg-gray-50'
                                            }`}
                                        >
                                            <input 
                                                type="checkbox" 
                                                name="pages" 
                                                checked={isSelected}
                                                onChange={handleCheckboxChange}
                                                value={deliverable} 
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-4 h-4 mt-0.5 text-red-600 bg-white border-gray-300 rounded focus:ring-red-500 flex-shrink-0" 
                                            />
                                            <span className="ms-2 text-xs leading-snug">{deliverable}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            stepNumber: 4,
            badge: "Step 4 of 4",
            title: "Design, SEO & Final Submit",
            shortName: "4. Submit",
            icon: FaSlidersH,
            content: (
                <div className="space-y-3">
                    <div>
                        <label className="font-semibold block mb-1 text-sm text-gray-800">
                            Design Aesthetic & UI / LLM Preferences: <span className="text-red-600 font-bold">*</span>
                        </label>
                        <textarea 
                            name="design_preferences" 
                            id="design_preferences"
                            value={formData.design_preferences}
                            onChange={handleChange} 
                            placeholder="e.g. Minimalist luxury theme, dark mode accents, Bento grid UI, GPT-4o / Claude 3.5 integrations..." 
                            rows="2" 
                            required
                            className={`w-full p-3 border rounded-xl bg-white text-gray-900 shadow-sm focus:outline-none focus:ring-2 transition ${
                                errors.design_preferences ? 'border-red-500 focus:ring-red-500 bg-red-50/20' : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                            }`}
                        ></textarea>
                        {errors.design_preferences && (
                            <p className="text-red-600 text-xs mt-1 flex items-center gap-1 font-medium">
                                <FaExclamationCircle className="text-[11px]" /> {errors.design_preferences}
                            </p>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="font-semibold block mb-1 text-sm text-gray-800">
                                SEO & Core Web Vitals: <span className="text-red-600 font-bold">*</span>
                            </label>
                            <select 
                                name="seo" 
                                id="seo"
                                value={formData.seo}
                                onChange={handleChange}
                                required
                                className="w-full p-3 border border-gray-300 rounded-xl bg-white text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                            >
                                <option value="Yes">Yes, maximize SEO & load speed</option>
                                <option value="No">No, internal application</option>
                                <option value="Unsure">Unsure / Need recommendation</option>
                            </select>
                        </div>
                        <div>
                            <label className="font-semibold block mb-1 text-sm text-gray-800">
                                Mobile / Tablet Responsive: <span className="text-red-600 font-bold">*</span>
                            </label>
                            <select 
                                name="responsive" 
                                id="responsive"
                                value={formData.responsive}
                                onChange={handleChange}
                                required
                                className="w-full p-3 border border-gray-300 rounded-xl bg-white text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                            >
                                <option value="Yes">Yes (100% Mobile Responsive)</option>
                                <option value="No">No (Desktop only)</option>
                                <option value="Unsure">Unsure</option>
                            </select>
                        </div>
                    </div>

                    {/* Pre-Submission Review Box */}
                    <div className="bg-red-50/90 border border-red-200 rounded-2xl p-3.5 mt-2">
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs font-bold text-red-700 uppercase tracking-wider">
                                Review & Confirm Scope
                            </span>
                            <span className="text-[11px] font-bold text-red-700 bg-white px-2.5 py-0.5 rounded-full border border-red-200 shadow-sm">
                                {formData.platform.split('(')[0].trim()}
                            </span>
                        </div>
                        <p className="text-xs text-gray-700 leading-relaxed">
                            Submitting requirement specification for <strong>{formData.client_name || 'Client'}</strong> (<span className="text-red-600">{formData.email || 'email not set'}</span>).
                            {formData.pages.length > 0 ? ` Includes ${formData.pages.length} selected deliverables.` : ' (Please select deliverables in Step 3)'}
                        </p>
                    </div>
                </div>
            )
        }
    ];
   
    const handleNext = () => {
        // Validate current section before proceeding
        const isValid = validateStep(currentSection);
        if (!isValid) return;

        if (currentSection < sections.length - 1) {
            setCurrentSection(prev => prev + 1);
        }
    };

    const handleBack = () => {
        if (currentSection > 0) {
            setCurrentSection(prev => prev - 1);
        }
    };

    const handleTabClick = (targetIdx) => {
        // If jumping forward, validate each prior step
        if (targetIdx > currentSection) {
            for (let i = 0; i < targetIdx; i++) {
                const stepValid = validateStep(i);
                if (!stepValid) {
                    setCurrentSection(i);
                    return;
                }
            }
        }
        setCurrentSection(targetIdx);
    };

    const handleSubmit = async (event) => {
        if (event) {
            event.preventDefault();
        }

        // Full validation of all 4 steps before API submission
        for (let i = 0; i < sections.length; i++) {
            const stepValid = validateStep(i);
            if (!stepValid) {
                setCurrentSection(i);
                return;
            }
        }

        setIsSubmitting(true);
        try {
            const data = await submitRequirementForm(formData);
            if (data.success) {
                toast.success(data.message || "Project requirements submitted successfully!");
                setFormData(initialFormData);
                setCurrentSection(0);
                setErrors({});
            } else {
                toast.error(data.error || "Error submitting form. Please try again.");
            }
        } catch (error) {
            console.error("Error submitting requirement form:", error);
            const errMsg = error.response?.data?.error || "Error submitting form. Please check your network connection.";
            toast.error(errMsg);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const progressPercentage = ((currentSection + 1) / sections.length) * 100;

    return (
        <div className="block md:flex w-full min-h-[550px] items-center py-8 px-4 max-w-7xl mx-auto">
            {/* Left Image Section */}
            <div className="w-full hidden md:flex md:w-1/2 justify-center items-center p-6">
                <img src="/images/form.svg" alt="Project Requirements Form" className="w-full max-h-[480px] object-contain" />
            </div>

            {/* Right Form Section */}
            <div className="w-full md:w-1/2 flex items-center justify-center p-2 sm:p-5">
                <div className="bg-white border border-gray-200 shadow-xl p-6 sm:p-8 rounded-2xl w-full max-w-lg">
                    
                    {/* Step Navigation Tabs */}
                    <div className="grid grid-cols-4 gap-1.5 mb-5">
                        {sections.map((sec, idx) => {
                            const isCompleted = idx < currentSection;
                            const isCurrent = idx === currentSection;
                            return (
                                <button
                                    key={sec.stepNumber}
                                    type="button"
                                    onClick={() => handleTabClick(idx)}
                                    className={`py-2 px-1 text-center rounded-xl text-xs font-bold transition flex flex-col items-center gap-1 ${
                                        isCurrent 
                                            ? 'bg-red-600 text-white shadow-md' 
                                            : isCompleted 
                                                ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200' 
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    <div className="flex items-center gap-1">
                                        {isCompleted ? <FaCheck className="text-[10px]" /> : <span>{sec.stepNumber}</span>}
                                    </div>
                                    <span className="truncate max-w-full text-[10px] uppercase font-semibold">{sec.shortName}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-5">
                        <div className="flex justify-between items-center mb-1.5">
                            <span className="text-xs font-semibold uppercase text-gray-500 tracking-wider">
                                {sections[currentSection].badge} — {sections[currentSection].title}
                            </span>
                            <span className="text-xs font-bold text-red-600">{Math.round(progressPercentage)}%</span>
                        </div>
                        <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
                            <div 
                                className="bg-red-600 h-2 rounded-full transition-all duration-300 ease-out" 
                                style={{ width: `${progressPercentage}%` }}
                            ></div>
                        </div>
                    </div>

                    <h1 className="text-gray-900 font-bold text-lg sm:text-xl mb-4 border-b pb-2 flex items-center gap-2">
                        <span className="w-2.5 h-5 bg-red-600 rounded-full inline-block"></span>
                        {sections[currentSection].title}
                    </h1>

                    <form 
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (currentSection < sections.length - 1) {
                                handleNext();
                            } else {
                                handleSubmit(e);
                            }
                        }}
                    >
                        <div>
                            {sections[currentSection].content}
                        </div>

                        <div className="flex justify-between mt-6 gap-3 pt-4 border-t border-gray-100">
                            <button 
                                type="button" 
                                onClick={handleBack} 
                                className={`py-2.5 px-4 flex items-center gap-2 rounded-xl text-sm font-bold transition ${
                                    currentSection === 0 
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                                }`} 
                                disabled={currentSection === 0 || isSubmitting}
                            >
                                <FaChevronLeft className="text-xs" /> Back
                            </button>

                            {currentSection < sections.length - 1 ? (
                                <button 
                                    type="button" 
                                    onClick={handleNext} 
                                    className="bg-red-600 flex items-center gap-2 text-white font-bold text-sm py-2.5 px-6 rounded-xl hover:bg-red-700 shadow-md transition"
                                >
                                    Next: {sections[currentSection + 1].shortName} <FaArrowRight className="text-xs" />
                                </button>
                            ) : (
                                <button 
                                    type="submit" 
                                    disabled={isSubmitting}
                                    className="bg-red-600 flex items-center justify-center gap-2 text-white font-bold text-sm py-2.5 px-6 rounded-xl hover:bg-red-700 shadow-lg transition disabled:opacity-60"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FaSpinner className="animate-spin text-xs" /> Submitting...
                                        </>
                                    ) : (
                                        'Submit Project Request'
                                    )}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
            <ToastContainer position="bottom-right" autoClose={4000} />
            <div className="w-full block md:hidden mt-6">
                <img src="/images/form.svg" alt="Project Requirements" className="w-full max-h-[300px] object-contain" />
            </div>
        </div>
    );
};

export default ReqForm;
